import React, { useState, useEffect, useCallback } from 'react';
import { TrendingUp, TrendingDown, Minus, Loader2, RefreshCw, BookOpen } from 'lucide-react';
import Tip from '../../components/Tip';

const API_URL = process.env.REACT_APP_API_URL || 'https://tradesavvy-production.up.railway.app';

interface WatchlistItem {
  pair: string;
  symbol: string;
  name: string;
}

interface SRLevel {
  price: number;
  touches: number;
}

interface VolumeAnalysis {
  current_vs_avg: number;
  spike: boolean;
}

interface TimeframeResult {
  signal: 'BUY' | 'SELL' | 'HOLD';
  confidence: number;
  trend: 'UPTREND' | 'DOWNTREND' | 'RANGE' | 'NEUTRAL';
  trend_strength: number;
  support_levels: SRLevel[];
  resistance_levels: SRLevel[];
  entry_price: number | null;
  stop_loss: number | null;
  take_profit: number | null;
  risk_reward: number | null;
  volume_analysis: VolumeAnalysis;
  conviction: 'HIGH' | 'LOW' | null;
  error?: string;
}

interface CoinResult {
  symbol: string;
  price: number;
  change24h: number;
  timeframes: Record<string, TimeframeResult>;
}

interface PriceActionTabProps {
  watchlist: WatchlistItem[];
  apiUrl?: string;
}

const TIMEFRAMES = ['1h', '4h', '1d'];

const PriceActionTab: React.FC<PriceActionTabProps> = ({ watchlist, apiUrl }) => {
  const [data, setData] = useState<CoinResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedTf, setSelectedTf] = useState('4h');
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  const url = apiUrl || API_URL;

  const fetchData = useCallback(async () => {
    if (watchlist.length === 0) return;
    setLoading(true);
    try {
      const symbols = watchlist.map(w => w.pair).join(',');
      const response = await fetch(`${url}/api/signals/priceaction?symbols=${symbols}`, {
        signal: AbortSignal.timeout(60000),
      });
      const result = await response.json();
      setData(Array.isArray(result) ? result : []);
      setLastRefresh(new Date());
    } catch (e) {
      console.error('Price Action fetch error:', e);
    }
    setLoading(false);
  }, [watchlist, url]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const nameMap: Record<string, string> = {};
  watchlist.forEach(w => { nameMap[w.symbol] = w.name; });

  const formatPrice = (price: number): string => {
    if (price === 0) return '0.00';
    if (price >= 1) return price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const decimals = Math.max(2, -Math.floor(Math.log10(price)) + 2);
    return price.toFixed(Math.min(decimals, 8));
  };

  const getSignalColor = (signal: string) => {
    if (signal === 'BUY') return 'text-green-500';
    if (signal === 'SELL') return 'text-red-500';
    return 'text-gray-400';
  };

  const getSignalIcon = (signal: string) => {
    if (signal === 'BUY') return <TrendingUp className="w-5 h-5" />;
    if (signal === 'SELL') return <TrendingDown className="w-5 h-5" />;
    return <Minus className="w-5 h-5" />;
  };

  const getTrendBadge = (trend: string) => {
    const colors: Record<string, string> = {
      UPTREND: 'bg-green-500/10 text-green-500',
      DOWNTREND: 'bg-red-500/10 text-red-500',
      RANGE: 'bg-yellow-500/10 text-yellow-400',
      NEUTRAL: 'bg-gray-500/10 text-gray-400',
    };
    return colors[trend] || colors.NEUTRAL;
  };

  const getRRColor = (rr: number) => {
    if (rr >= 3) return 'text-green-400';
    if (rr >= 2) return 'text-green-500';
    if (rr >= 1.5) return 'text-yellow-400';
    return 'text-red-400';
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div className="flex gap-1.5">
          {TIMEFRAMES.map(tf => (
            <button
              key={tf}
              onClick={() => setSelectedTf(tf)}
              className={`px-3 py-1.5 rounded-md text-sm font-mono font-semibold transition-all ${
                selectedTf === tf
                  ? 'bg-blue-500 text-white shadow-lg shadow-blue-500/25'
                  : 'bg-gray-800 text-gray-500 border border-gray-700 hover:text-white hover:border-gray-500'
              }`}
            >
              {tf}
            </button>
          ))}
        </div>
        <button
          onClick={fetchData}
          disabled={loading}
          className="flex items-center gap-1 px-3 py-1.5 bg-gray-800 border border-gray-700 hover:border-gray-600 rounded text-xs text-gray-300 hover:text-white transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
          {loading ? 'Loading...' : 'Refresh'}
        </button>
      </div>

      <div className="flex items-center gap-3 mb-3 px-3 py-2 bg-blue-500/5 border border-blue-500/10 rounded-lg">
        <span className="text-xs text-gray-400">Swing S/R, trend structure, R:R setups</span>
        <a href="/guides/guide-priceaction.html" target="_blank" rel="noopener noreferrer"
          className="flex items-center gap-1.5 px-2.5 py-1 bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 rounded text-xs font-medium transition-colors whitespace-nowrap">
          <BookOpen className="w-3.5 h-3.5" />
          How to read this
        </a>
      </div>

      {lastRefresh && (
        <div style={{ marginBottom: 12, fontSize: 11, color: '#52525b' }}>
          Updated: {lastRefresh.toLocaleTimeString()} &middot; Auto-refreshes every 60s
        </div>
      )}

      {loading && data.length === 0 && (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-7 h-7 text-blue-500 animate-spin mr-3" />
          <span className="text-gray-400 text-sm">Analyzing price action...</span>
        </div>
      )}

      <div className="grid gap-5">
        {data.map(coin => {
          const tfData = coin.timeframes?.[selectedTf];
          if (!tfData) return null;

          return (
            <div key={coin.symbol} className="bg-gray-900 border border-gray-800 rounded-lg p-5 hover:border-gray-700 transition-colors">
              {/* Header */}
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-xl font-bold">{coin.symbol}-USD</h2>
                  <p className="text-gray-400 text-xs">{nameMap[coin.symbol] || coin.symbol}</p>
                </div>
                <div className="text-right">
                  <div className="text-xl font-mono">${formatPrice(coin.price)}</div>
                  <div className={`text-xs ${coin.change24h >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                    {coin.change24h >= 0 ? '+' : ''}{coin.change24h.toFixed(2)}%
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Trend + Signal */}
                <div className="bg-gray-800/50 rounded-lg p-3 border border-gray-700">
                  <div className="text-xs text-gray-400 uppercase mb-2">Trend &amp; Signal <Tip text="Trend detected from swing structure: Higher Highs + Higher Lows = UPTREND, Lower Lows + Lower Highs = DOWNTREND, mixed = RANGE. Strength % shows how consistent the pattern is." /></div>
                  <div className={`flex items-center gap-2 mb-3 ${getSignalColor(tfData.signal)}`}>
                    {getSignalIcon(tfData.signal)}
                    <span className="font-bold">{tfData.signal}</span>
                    <span className="ml-auto text-xs text-gray-400">Confidence <Tip text="Setup confidence, capped at 80%. Based on trend strength + volume spike bonus. Higher when trend is consistent and volume confirms." /> <span className="font-mono">{tfData.confidence}%</span></span>
                  </div>

                  {/* Trend badge with strength */}
                  <div className={`text-xs px-2 py-1 rounded text-center mb-3 ${getTrendBadge(tfData.trend)}`}>
                    {tfData.trend}
                    {tfData.trend_strength > 0 && (
                      <span className="ml-1 opacity-70">({tfData.trend_strength}%)</span>
                    )}
                  </div>

                  {/* Volume */}
                  <div className="text-xs">
                    <div className="flex justify-between mb-1">
                      <span className="text-gray-500">Volume vs Avg <Tip text="Current volume divided by 20-period average. 2.0x+ = SPIKE (shown in yellow). Volume spikes at key levels confirm the move is real. Low volume moves are more likely to reverse." /></span>
                      <span className={`font-mono ${tfData.volume_analysis.spike ? 'text-yellow-400' : 'text-gray-400'}`}>
                        {tfData.volume_analysis.current_vs_avg}x
                        {tfData.volume_analysis.spike && ' SPIKE'}
                      </span>
                    </div>
                    <div className="w-full bg-gray-700 rounded-full h-1.5">
                      <div
                        className={`h-1.5 rounded-full ${tfData.volume_analysis.spike ? 'bg-yellow-400' : 'bg-gray-500'}`}
                        style={{ width: `${Math.min(100, tfData.volume_analysis.current_vs_avg * 33)}%` }}
                      />
                    </div>
                  </div>
                </div>

                {/* S/R Levels */}
                <div className="bg-gray-800/50 rounded-lg p-3 border border-gray-700">
                  <div className="text-xs text-gray-400 uppercase mb-2">Support &amp; Resistance <Tip text="Key price levels from clustered swing points (within 0.5%). Support = where buying stopped declines. Resistance = where selling stopped rallies. More touches = stronger level." /></div>

                  {/* Resistance */}
                  <div className="mb-3">
                    <div className="text-[10px] text-red-400/60 uppercase mb-1">Resistance</div>
                    {tfData.resistance_levels.length === 0 ? (
                      <div className="text-xs text-gray-600">None detected</div>
                    ) : (
                      <div className="space-y-0.5">
                        {tfData.resistance_levels.slice(0, 3).map((r, i) => (
                          <div key={i} className="flex justify-between items-center text-xs">
                            <span className="font-mono text-red-400">${formatPrice(r.price)}</span>
                            <div className="flex items-center gap-1">
                              <div className="flex gap-px">
                                {Array.from({ length: Math.min(r.touches, 5) }).map((_, j) => (
                                  <div key={j} className="w-1.5 h-1.5 rounded-full bg-red-400/50" />
                                ))}
                              </div>
                              <span className="text-gray-500 text-[10px]">{r.touches}x</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Current price marker */}
                  <div className="flex items-center gap-2 mb-3 py-1 border-y border-gray-700">
                    <div className="w-2 h-2 rounded-full bg-blue-400" />
                    <span className="text-xs font-mono text-blue-400">${formatPrice(coin.price)}</span>
                    <span className="text-[10px] text-gray-500">Current</span>
                  </div>

                  {/* Support */}
                  <div>
                    <div className="text-[10px] text-green-400/60 uppercase mb-1">Support</div>
                    {tfData.support_levels.length === 0 ? (
                      <div className="text-xs text-gray-600">None detected</div>
                    ) : (
                      <div className="space-y-0.5">
                        {tfData.support_levels.slice(0, 3).map((s, i) => (
                          <div key={i} className="flex justify-between items-center text-xs">
                            <span className="font-mono text-green-400">${formatPrice(s.price)}</span>
                            <div className="flex items-center gap-1">
                              <div className="flex gap-px">
                                {Array.from({ length: Math.min(s.touches, 5) }).map((_, j) => (
                                  <div key={j} className="w-1.5 h-1.5 rounded-full bg-green-400/50" />
                                ))}
                              </div>
                              <span className="text-gray-500 text-[10px]">{s.touches}x</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Trade Setup */}
                <div className="bg-gray-800/50 rounded-lg p-3 border border-gray-700">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs text-gray-400 uppercase">Trade Setup <Tip text="Only generated when R:R >= 2.0 (potential gain at least 2x the risk). Entry at nearest S/R level, stop loss 1.5x ATR beyond, take profit at opposing S/R level." /></span>
                    {tfData.conviction === 'HIGH' && (
                      <span className="px-1.5 py-0.5 bg-green-500/15 text-green-400 rounded text-[10px] font-bold border border-green-500/20">HIGH CONVICTION</span>
                    )}
                    {tfData.conviction === 'LOW' && (
                      <span className="px-1.5 py-0.5 bg-yellow-500/15 text-yellow-500 rounded text-[10px] font-bold border border-yellow-500/20">LOW CONVICTION</span>
                    )}
                    {tfData.conviction && (
                      <Tip text="HIGH = confidence ≥ 60% + R:R ≥ 2.0. Price Action already requires R:R ≥ 2.0 to fire a signal, so HIGH means a strong trend (higher-highs/lows confirming direction). LOW = signal fired but trend strength is moderate." />
                    )}
                  </div>
                  {tfData.entry_price && tfData.risk_reward ? (
                    <div>
                      {/* R:R large display */}
                      <div className="text-center mb-3">
                        <div className={`text-3xl font-bold font-mono ${getRRColor(tfData.risk_reward)}`}>
                          {tfData.risk_reward.toFixed(1)}R
                        </div>
                        <div className="text-[10px] text-gray-500">Risk/Reward <Tip text="Reward divided by risk. At 2:1 R:R you only need to win 34% of trades to be profitable. At 3:1 you only need 26%. Below 2.0 the system won't signal." /></div>
                      </div>

                      <div className="space-y-2 text-xs">
                        <div className="flex justify-between">
                          <span className="text-gray-500">Entry</span>
                          <span className="font-mono text-blue-400">${formatPrice(tfData.entry_price)}</span>
                        </div>
                        {tfData.stop_loss && (
                          <div className="flex justify-between">
                            <span className="text-gray-500">Stop Loss</span>
                            <span className="font-mono text-red-400">${formatPrice(tfData.stop_loss)}</span>
                          </div>
                        )}
                        {tfData.take_profit && (
                          <div className="flex justify-between">
                            <span className="text-gray-500">Take Profit</span>
                            <span className="font-mono text-green-400">${formatPrice(tfData.take_profit)}</span>
                          </div>
                        )}
                      </div>

                      {tfData.risk_reward < 2 && (
                        <div className="mt-2 p-1.5 rounded bg-red-500/5 border border-red-500/10 text-[10px] text-red-400 text-center">
                          R:R below 2.0 threshold
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-28">
                      <div className="text-center">
                        <div className="text-xs text-gray-600 mb-1">No active setup</div>
                        <div className="text-[10px] text-gray-700">
                          {tfData.trend === 'RANGE' ? 'Market ranging — waiting for trend' :
                           'Waiting for pullback to key level'}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PriceActionTab;
